import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SocratesLogo } from '@/components/SocratesLogo';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  
  const { resetPassword, signInWithMagicLink } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await resetPassword(email);

    if (error) {
      // Account may not have a password provider (e.g. created via invite/magic link).
      // Fall back to sending a magic login link so the user can still get in.
      const { error: magicError } = await signInWithMagicLink(email, `${window.location.origin}/reset-password`);

      if (magicError) {
        toast({
          title: 'Failed to send reset email',
          description: magicError.message,
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }
    }

    setSent(true);
    setLoading(false);
  };

  const handleResend = async () => {
    setLoading(true);
    const { error } = await resetPassword(email);
    if (error) {
      await signInWithMagicLink(email, `${window.location.origin}/reset-password`);
    }
    setLoading(false);
    toast({
      title: 'Link resent',
      description: 'Check your email for a new reset link.',
    });
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-green-100 p-3 rounded-full">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Check your email</h2>
          <p className="text-stone-600 mb-4">
            If an account exists for <strong>{email}</strong>, we sent a password reset link.
          </p>
          <p className="text-sm text-stone-500 mb-6">
            Check your spam folder if you don't see it within a few minutes.
          </p>
          <div className="space-y-2">
            <Button
              variant="outline"
              onClick={handleResend}
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resending...
                </>
              ) : (
                "Didn't get the email? Resend"
              )}
            </Button>
            <Link to="/login">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <SocratesLogo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-stone-800">Reset Password</h1>
          <p className="text-stone-600">Enter your email to receive a reset link</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send reset link'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-stone-600">
          Remember your password?{' '}
          <Link to="/login" className="font-medium text-stone-800 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}




